import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Group, TextInput } from '@tidbcloud/uikit'
import { TimeRangePicker } from '@tidbcloud/uikit/biz'
import { IconXClose } from '@tidbcloud/uikit/icons'
import dayjs from 'dayjs'

import { eventTracking } from '~/utils/telemetry'

import { FilterMultiSelect } from './FilterMultiSelect'
import { formatStatementUserLabel } from './statementV2'
import { useStatementV2UrlState } from './url-state'

interface StatementV2FiltersProps {
  isLoading: boolean
  stmtKindOptions: string[]
  dbUserOptions: string[]
  dbOptions: string[]
  tableOptions: string[]
  stmtKindsLoading?: boolean
  dbUsersLoading?: boolean
  dbsLoading?: boolean
  tablesLoading?: boolean
  onStmtKindsOpen?: () => void
  onDbUsersOpen?: () => void
  onDbsOpen?: () => void
  onTablesOpen?: () => void
  stripUserPrefix: boolean
}

const FEATURE_NAME = 'SQL Statement V2'
const QUICK_RANGES = [
  15 * 60,
  30 * 60,
  60 * 60,
  2 * 60 * 60,
  6 * 60 * 60,
  12 * 60 * 60,
  24 * 60 * 60,
  2 * 24 * 60 * 60,
  3 * 24 * 60 * 60,
  7 * 24 * 60 * 60
]

const normalizeTableNames = (values: string[]) => [
  ...new Set(
    values
      .flatMap((value) => value.split(',').map((table) => table.trim()))
      .filter(Boolean)
  )
]

export default function StatementFilters({
  isLoading,
  stmtKindOptions,
  dbUserOptions,
  dbOptions,
  tableOptions,
  stmtKindsLoading,
  dbUsersLoading,
  dbsLoading,
  tablesLoading,
  onStmtKindsOpen,
  onDbUsersOpen,
  onDbsOpen,
  onTablesOpen,
  stripUserPrefix
}: StatementV2FiltersProps) {
  const {
    timeRange,
    setTimeRange,
    kinds,
    setKinds,
    search,
    setSearch,
    dbs,
    setDbs,
    sampleUsers,
    setSampleUsers,
    tableNames,
    setTableNames
  } = useStatementV2UrlState()
  const [keyword, setKeyword] = useState(search)

  useEffect(() => setKeyword(search), [search])

  const getDbUserLabel = useCallback(
    (user: string) => formatStatementUserLabel(user, stripUserPrefix),
    [stripUserPrefix]
  )
  const normalizedTables = useMemo(
    () => normalizeTableNames(tableNames),
    [tableNames]
  )

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        setSearch(keyword)
        eventTracking(`${FEATURE_NAME} Keyword Filter Changed`)
      }}
    >
      <Group>
        <TimeRangePicker
          value={timeRange}
          onChange={(value) => {
            setTimeRange(value)
            eventTracking(`${FEATURE_NAME} Time Range Filter Clicked`, {
              timeRange: value
            })
          }}
          quickRanges={QUICK_RANGES}
          maxDuration={QUICK_RANGES[QUICK_RANGES.length - 1]}
          minDateTime={() => dayjs().subtract(30, 'day').toDate()}
          maxDateTime={() => dayjs().toDate()}
          loading={isLoading}
        />

        <FilterMultiSelect
          kind='Statement Kinds'
          width={200}
          value={kinds}
          data={stmtKindOptions.map((value) => ({ label: value, value }))}
          onChange={(value) => {
            eventTracking(`${FEATURE_NAME} Kind Filter Clicked`)
            setKinds(value)
          }}
          loading={stmtKindsLoading}
          onDropdownOpen={onStmtKindsOpen}
        />

        <FilterMultiSelect
          kind='DB User'
          width={180}
          value={sampleUsers}
          data={dbUserOptions.map((value) => ({
            label: getDbUserLabel(value),
            value
          }))}
          onChange={(value) => {
            eventTracking(`${FEATURE_NAME} DB User Filter Changed`)
            setSampleUsers(value)
          }}
          loading={dbUsersLoading}
          onDropdownOpen={onDbUsersOpen}
          getValueLabel={getDbUserLabel}
          singleSelect
        />

        <FilterMultiSelect
          kind='DBs'
          width={180}
          value={dbs}
          data={dbOptions.map((value) => ({ label: value, value }))}
          onChange={(value) => {
            eventTracking(`${FEATURE_NAME} DB Filter Changed`)
            setDbs(value)
          }}
          loading={dbsLoading}
          onDropdownOpen={onDbsOpen}
        />

        <FilterMultiSelect
          kind='Tables'
          width={180}
          value={normalizedTables}
          data={normalizeTableNames(tableOptions).map((value) => ({
            label: value,
            value
          }))}
          onChange={(value) => {
            eventTracking(`${FEATURE_NAME} Table Filter Changed`)
            setTableNames(normalizeTableNames(value))
          }}
          loading={tablesLoading}
          onDropdownOpen={onTablesOpen}
        />

        <TextInput
          w={200}
          value={keyword}
          onChange={(event) => setKeyword(event.currentTarget.value)}
          placeholder='Filter Keyword'
          rightSection={
            keyword ? (
              <IconXClose
                style={{ cursor: 'pointer' }}
                size={14}
                onClick={() => {
                  setKeyword('')
                  setSearch('')
                }}
              />
            ) : undefined
          }
          disabled={isLoading}
        />

        <button type='submit' style={{ display: 'none' }} />
      </Group>
    </form>
  )
}
